import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Search, User, MessageCircle, LogOut, UtensilsCrossed, Bell, ShieldCheck } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Avatar } from './Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setUnreadNotifs(0);
      return;
    }
    const fetchCounts = async () => {
      try {
        const [msgRes, notifRes] = await Promise.all([
          axios.get(`${API}/messages/unread-count`, { withCredentials: true }),
          axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
        ]);
        setUnreadCount(msgRes.data.unread || 0);
        setUnreadNotifs(notifRes.data.unread || 0);
      } catch (e) {
        // ignore
      }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 4000);
    return () => clearInterval(interval);
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/listings?search=${searchQuery}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <nav 
      className="sticky top-0 z-50 border-b border-[#E5E0D8]" 
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(24px)' }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <UtensilsCrossed size={28} strokeWidth={1.5} style={{ color: '#D95A41' }} />
            <span 
              className="text-2xl tracking-tight" 
              style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
            >
              eatfromlocal
            </span>
          </Link>

          {location.pathname !== '/login' && location.pathname !== '/register' && (
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={18} style={{ color: '#5C5C5C', strokeWidth: 1.5 }} />
                <input
                  type="text"
                  placeholder="Search for food, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', fontSize: '14px', backgroundColor: '#F9F6F0' }}
                  data-testid="search-input"
                />
              </div>
            </form>
          )}

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/blog" className="hidden md:inline-block">
              <Button
                variant="ghost"
                style={{ fontFamily: 'Manrope', fontWeight: 500 }}
                data-testid="blog-nav-btn"
              >
                Blog
              </Button>
            </Link>
            <Link to="/contact" className="hidden md:inline-block">
              <Button
                variant="ghost"
                style={{ fontFamily: 'Manrope', fontWeight: 500 }}
                data-testid="contact-nav-btn"
              >
                Contact
              </Button>
            </Link>
            {user ? (
              <>
                <Link to="/listings">
                  <Button 
                    variant="ghost" 
                    className="hidden md:flex"
                    style={{ fontFamily: 'Manrope', fontWeight: 500 }}
                    data-testid="explore-listings-btn"
                  >
                    Explore Listings
                  </Button>
                </Link>
                <Link to="/post">
                  <Button 
                    className="rounded-md px-3 sm:px-4" 
                    style={{ 
                      backgroundColor: '#D95A41', 
                      fontFamily: 'Manrope', 
                      fontWeight: 600,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#C04E38'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#D95A41'}
                    data-testid="post-meal-btn"
                  >
                    <span className="hidden sm:inline">Post a Meal</span>
                    <span className="sm:hidden">Post</span>
                  </Button>
                </Link>
                <Link to="/notifications" className="relative">
                  <Button variant="ghost" size="icon" data-testid="notifications-btn">
                    <Bell size={20} strokeWidth={1.5} />
                  </Button>
                  {unreadNotifs > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: '#D95A41',
                        color: '#FFFFFF',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 5px',
                        fontFamily: 'Manrope',
                        fontSize: '11px',
                        fontWeight: 700,
                        border: '2px solid #FFFFFF'
                      }}
                      data-testid="notifications-badge"
                    >
                      {unreadNotifs > 9 ? '9+' : unreadNotifs}
                    </span>
                  )}
                </Link>
                <Link to="/messages" className="relative">
                  <Button variant="ghost" size="icon" data-testid="messages-btn">
                    <MessageCircle size={20} strokeWidth={1.5} />
                  </Button>
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: '#D95A41',
                        color: '#FFFFFF',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 5px',
                        fontFamily: 'Manrope',
                        fontSize: '11px',
                        fontWeight: 700,
                        border: '2px solid #FFFFFF'
                      }}
                      data-testid="unread-badge"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="rounded-full hover:opacity-80" style={{ transition: 'opacity 0.2s' }} data-testid="user-menu-trigger">
                      <Avatar name={user.name} imageUrl={user.profile_picture} size={36} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48" data-testid="user-menu">
                    {user.is_admin && (
                      <>
                        <DropdownMenuItem
                          onClick={() => navigate('/admin-dashboard')}
                          data-testid="admin-dashboard-menu-item"
                          style={{ color: '#D95A41', fontWeight: 600 }}
                        >
                          <ShieldCheck size={16} className="mr-2" />
                          Admin Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/my-listings')} data-testid="my-listings-menu-item">
                      My Listings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="alerts-menu-item">
                      Alerts
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/profile/${user.id}`)} data-testid="profile-menu-item">
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" style={{ fontFamily: 'Manrope' }} data-testid="login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="rounded-md" 
                    style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
                    data-testid="signup-btn"
                  >
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};